/**
 * The pre-pass: for each item light list, show only the meshes of the lights on it and let the
 * mounted tree draw itself into that list's accumulators. Each list takes up to three passes:
 * `CanvasLightPass.tsx` says why.
 */
import { LIGHT_PASS_LAYER } from './lightPassLayers.js';
import { drawsInPass, } from './itemLightList.js';
import { listWindow, windowScissor } from './lightListWindow.js';
import { beginStep, endStep } from '../frameProfile/frameProfile.js';
/**
 * Sizes `list`'s buffers to the drawing buffer and clips them to its window, or lets them draw
 * whole where it has none. Clipped after the resize, which resets a target's scissor. An array
 * target keeps its layer count.
 */
function fitListTargets(list, windows, camera, size) {
    const window = listWindow(list.placementIds, windows);
    const scissor = window ? windowScissor(window, camera, size) : null;
    const targets = [
        list.target,
        list.lightOnlyTarget,
        list.shadowTintTarget,
        list.apart?.target,
        list.apart?.shadowTintTarget,
    ];
    for (const target of targets) {
        if (!target)
            continue;
        if (target.width !== size.x || target.height !== size.y)
            target.setSize(size.x, size.y, target.depth);
        target.scissorTest = scissor !== null;
        if (scissor)
            target.scissor.copy(scissor);
    }
}
function showListMeshes(passMeshes, list, kind) {
    for (const { mesh, ordinal, role } of passMeshes) {
        mesh.visible = drawsInPass(list.entries.get(ordinal), role, kind);
    }
}
function showLightMeshes(passMeshes, entry, kind) {
    for (const { mesh, ordinal, role } of passMeshes) {
        mesh.visible = drawsInPass(ordinal === entry.ordinal ? entry : undefined, role, kind);
    }
}
const BLACK = [0, 0, 0];
/** Each light alone, from black: its layer then holds only its own `light_color` and alpha. */
function renderLightsApart(apart, passMeshes, render) {
    apart.entries.forEach((entry, layer) => {
        showLightMeshes(passMeshes, entry, 'light');
        render(apart.target, BLACK, layer);
        const tintLayer = apart.tintLayers[layer];
        if (tintLayer < 0)
            return;
        showLightMeshes(passMeshes, entry, 'tint');
        render(apart.shadowTintTarget, BLACK, tintLayer);
    });
}
/** Draws every list's buffers once: a frame's work. */
export function renderLightLists({ gl, scene, camera, lists, passMeshes, seedMaterial, canvasModulate, resolution, readBufferSize, windows, }) {
    if (lists.length === 0)
        return;
    // The targets and the lookup are all in device pixels, because that is what `gl_FragCoord` is
    // measured in. Sizing from the CSS size instead reads the buffer at the wrong scale on any
    // display where dpr is not 1.
    readBufferSize(resolution);
    const previousTarget = gl.getRenderTarget();
    const previousMask = camera.layers.mask;
    const previousAutoUpdate = scene.matrixWorldAutoUpdate;
    const seed = seedMaterial.uniforms.uSeed.value;
    const render = (target, seedRgb, layer = 0) => {
        seed.set(seedRgb[0], seedRgb[1], seedRgb[2]);
        gl.setRenderTarget(target, layer);
        // One clear per pass, not per light: each light stamps its own ref, so last frame's stamps
        // are the only ones that could be mistaken for this frame's. Leaving them would make a light
        // that has stopped casting keep the hole it cut.
        gl.clear(false, false, true);
        gl.render(scene, camera);
        // Only visibility changes between the passes, so the first render's world matrices hold.
        scene.matrixWorldAutoUpdate = false;
    };
    // `finally`, as in the SubViewport pass: a throw out of `gl.render` (a link failure, a lost
    // context) would leave the renderer on an accumulation buffer with most layers masked, and
    // r3f's main render would draw the scene into it: a black canvas, not one broken light.
    beginStep(gl, '2D lights');
    try {
        camera.layers.set(LIGHT_PASS_LAYER);
        for (const list of lists) {
            fitListTargets(list, windows, camera, resolution);
            showListMeshes(passMeshes, list, 'light');
            if (list.target)
                render(list.target, [canvasModulate.r, canvasModulate.g, canvasModulate.b]);
            // Light Only skips `color *= canvas_modulation`, so its accumulation is the same lights
            // over an unmodulated seed.
            if (list.lightOnlyTarget)
                render(list.lightOnlyTarget, [1, 1, 1]);
            if (list.shadowTintTarget) {
                // `light_shadow_compute` runs after `light_color.rgb *= base_color.rgb` and its `mix`
                // overwrites rgb, so the tint is albedo-free: Godot 4.6.3 adds the same 15/255 over 0.25
                // and 0.75 albedo. The black seed zeroes it without touching the renderer's clear colour.
                showListMeshes(passMeshes, list, 'tint');
                render(list.shadowTintTarget, BLACK);
            }
            if (list.apart)
                renderLightsApart(list.apart, passMeshes, render);
        }
    }
    finally {
        gl.setRenderTarget(previousTarget);
        camera.layers.mask = previousMask;
        scene.matrixWorldAutoUpdate = previousAutoUpdate;
        endStep(gl, '2D lights');
    }
}
//# sourceMappingURL=lightAccumulationPass.js.map